// i18n-ns: budget (validation messages are resolved through the caller's t, not a local useTranslation)
import { BudgetScenarioType } from '@callmart/types';

// Shared shape + validation for a scenario's metadata (Name, Beschreibung, Budget, Szenarioart,
// Start, Ende). Used by the create page, the detail page's header form and the duplicate dialog, so
// all three enforce the same rules the backend does.

export interface ScenarioMetaValue {
    name: string;
    description: string;
    type: BudgetScenarioType | '';
    budgetVersionId: number | '';
    startDate: string; // 'YYYY-MM-DD'
    endDate: string; // 'YYYY-MM-DD'
}

export const EMPTY_SCENARIO_META: ScenarioMetaValue = {
    name: '',
    description: '',
    type: '',
    budgetVersionId: '',
    startDate: '',
    endDate: '',
};

// Every scenario type shown on the German board.
export const SCENARIO_TYPES: BudgetScenarioType[] = [
    BudgetScenarioType.CONTACTS,
    BudgetScenarioType.MINUTE_PRICE,
    BudgetScenarioType.SHIFT,
    BudgetScenarioType.BUDGET_ADJUSTMENT,
    BudgetScenarioType.REACHABILITY_ADJUSTMENT,
];

// Mirrors the backend cap (MAX_SCENARIO_MONTHS): a typo'd end year would otherwise fan the impact
// computation out to thousands of months and hang the detail grid.
export const MAX_SCENARIO_MONTHS = 120;

/** A period's month count, INCLUSIVE of both ends — 1 for a single month, 12 for a calendar year.
 *  Shared with the Overview's `Datum von`/`bis` range (CALL-815), which is capped the same way: a
 *  typo'd end year would otherwise generate thousands of month columns. */
export const monthSpan = (startDate: string, endDate: string): number => {
    const [startYear, startMonth] = startDate.split('-').map(Number);
    const [endYear, endMonth] = endDate.split('-').map(Number);
    return (endYear - startYear) * 12 + (endMonth - startMonth) + 1;
};

type Translate = (key: string, options?: Record<string, unknown>) => string;

export const scenarioNameError = (name: string, t: Translate): string | null =>
    name.trim() === '' ? t('validation.scenarioNameRequired') : null;

/** What is wrong with the period, PER FIELD — so the form can mark the input that is wrong. */
export interface ScenarioDateErrors {
    startDate: string | null;
    endDate: string | null;
}

/** The period's problems, attributed to the field each belongs to (CALL-784).
 *
 *  This used to return one message and deliberately nothing at all while either date was missing, with
 *  `hasScenarioMetaErrors` blocking the save separately. The two then disagreed: clearing Ende (or leaving it
 *  half-typed, which the picker reports as no value) emptied the whole month block, left the field
 *  black-bordered with aria-invalid="false" and no helper text, and both action bars said "Bitte markierte
 *  Eingaben korrigieren" while nothing on the page was marked. An empty date is now a problem OF ITS FIELD,
 *  and hasScenarioMetaErrors is derived from this one answer. */
export const scenarioDateErrors = (startDate: string, endDate: string, t: Translate): ScenarioDateErrors => {
    if (!startDate || !endDate) {
        return {
            startDate: startDate ? null : t('validation.dateRequired'),
            endDate: endDate ? null : t('validation.dateRequired'),
        };
    }
    // Everything below is a property of the RANGE rather than of one date, and is reported on Ende: Start is
    // a legitimate value on its own, and Ende is the field the user moves to make the range work.
    if (endDate < startDate) return { startDate: null, endDate: t('validation.endBeforeStart') };
    return {
        startDate: null,
        endDate:
            monthSpan(startDate, endDate) > MAX_SCENARIO_MONTHS
                ? t('validation.scenarioPeriodTooLong', { max: MAX_SCENARIO_MONTHS })
                : null,
    };
};

/** True while the period cannot be used — by the save, and by the month columns derived from it. */
export const hasScenarioDateErrors = (startDate: string, endDate: string, t: Translate): boolean => {
    const errors = scenarioDateErrors(startDate, endDate, t);
    return Boolean(errors.startDate || errors.endDate);
};

export const hasScenarioMetaErrors = (value: ScenarioMetaValue, t: Translate): boolean =>
    Boolean(scenarioNameError(value.name, t)) ||
    hasScenarioDateErrors(value.startDate, value.endDate, t) ||
    !value.type ||
    value.budgetVersionId === '';

// The create payload, from a validated form value.
export const toScenarioPayload = (value: ScenarioMetaValue) => ({
    name: value.name.trim(),
    description: value.description.trim() || null,
    type: value.type as BudgetScenarioType,
    budgetVersionId: value.budgetVersionId as number,
    startDate: value.startDate,
    endDate: value.endDate,
});

// Every field a metadata PATCH can carry: the create payload except `type`, which is immutable once a
// scenario has type-specific input rows (the backend's update schema does not accept it either).
export type ScenarioMetaPatch = Omit<ReturnType<typeof toScenarioPayload>, 'type'>;

const normalisedMeta = (value: ScenarioMetaValue): ScenarioMetaPatch => {
    const { type: _type, ...patch } = toScenarioPayload(value);
    return patch;
};

/** The PATCH payload: only the fields whose value REALLY differs from the state the form was loaded in.
 *
 *  This used to send all of them, every save. A PATCH is a blind write of each field it names, so a save
 *  built on stale metadata re-asserted the stale value of every field the user never touched — and the
 *  metadata IS the stale half of the form now that `revision` travels with the inputs read (see
 *  useScenarioForm): the scenario query is read at T0, a colleague renames the scenario at T1, and the
 *  inputs GET answers at T2 with a fresh revision. The form is then "clean" by its own measure, its next
 *  save passes the concurrency guard, and the resent name silently reverts the rename — HTTP 200, no
 *  conflict. A field nobody edited is now absent from the request, so it cannot overwrite anyone.
 *
 *  Compared NORMALISED, i.e. as the payload the wire would carry: a name the user only padded with
 *  spaces would be stored exactly as it already is, so shipping it would risk the rename above for a
 *  change the server cannot even see. */
export const toScenarioMetaPatch = (
    value: ScenarioMetaValue,
    baseline: ScenarioMetaValue
): Partial<ScenarioMetaPatch> => {
    const next = normalisedMeta(value);
    const base = normalisedMeta(baseline);
    const patch: Partial<ScenarioMetaPatch> = {};
    // Keyed off the payload itself rather than a hand-written list, so a metadata field added later is
    // diffed — and therefore sent — without anyone having to remember this function.
    const keepIfChanged = <K extends keyof ScenarioMetaPatch>(key: K) => {
        if (next[key] !== base[key]) patch[key] = next[key];
    };
    (Object.keys(next) as Array<keyof ScenarioMetaPatch>).forEach(keepIfChanged);
    return patch;
};
